import { _ } from "../../../../core/polyglot/polyglot.js";
import { getColorShades } from "../../../../core/helpers/color-util.js";
import { text } from "../../../../core/elements/index.js";
import { Widget } from "@foretagsplatsen/widgetjs";
import AgedAccountsDoughnutChartWidget from "../AgedAccountsDoughnutChartWidget/AgedAccountsDoughnutChartWidget.js";
import AgedAccountsSummaryTableWidget from "../AgedAccountsSummaryTableWidget/AgedAccountsSummaryTableWidget.js";

/**
 * `AgedAccountsSummaryWidget` renders a period report aged accounts
 * section containing a chart, a table (as its legend), a link to the
 * corresponding aged report, and a warning if some values are negative (because
 * negative values are shown in the table, but not in the graph).
 *
 * @param {AgedAccountsSummary} summary - summary of all aged accounts
 * values sorted by periods.
 * @param {string} baseColor - Darker color that will be lightened to get the
 * different shades for each group (e.g., "#46473E").
 * @param {string} importDate - The date of the last import.
 * @param {string} linkLabel - Label that should rendered above the doughnut.
 * Clicking on it will redirect to the detailed summary.
 * @param {string} pathName - Link to the detailed summary.
 * @param {string} [textTotal] - The text labeling the total inside the doughnut.
 * @param {string} cssClass - Specific class for the pdf exporter.
 */
export default class AgedAccountsSummaryWidget extends Widget {
	constructor({
		summary,
		baseColor,
		importDate,
		linkLabel,
		pathName,
		textTotal,
		cssClass = "",
	}) {
		super(...arguments);

		this._linkLabel = linkLabel;
		this._pathName = pathName;
		this._cssClass = cssClass;
		this._importDate = importDate;
		this._hasNegativeValue = summary.hasNegativeValue();

		// We can't let the CSS handle colors for the doughnut SVG chart
		const colors = this._getColorShades(baseColor);

		this._summaryTable = new AgedAccountsSummaryTableWidget({
			summary,
			colors,
			mouseOverCallback: this._highlightGroup.bind(this),
		});

		this._doughnutWidget = new AgedAccountsDoughnutChartWidget({
			summary,
			colors,
			textTotal,
			mouseOverCallback: this._highlightGroup.bind(this),
		});
	}

	renderContentOn(html) {
		html.div(this._renderLinkOn.bind(this));

		html.div({ klass: this._cssClass }, [
			this._renderImportDateOn.bind(this),

			html.div(
				{ klass: "aged-accounts-summary" },
				this._doughnutWidget,
				this._summaryTable,
			),
		]);

		html.div(this._renderWarningOn.bind(this));
	}

	_highlightGroup(group) {
		this._doughnutWidget.highlightGroup(group);
		this._summaryTable.highlightGroup(group);
	}

	_renderImportDateOn(html) {
		html.render(
			text(
				{
					dataTest: "import-date",
				},
				_("Last import date: {date}", { date: this._importDate }),
			),
		);
	}

	_renderLinkOn(html) {
		html.a(
			{
				klass: "aged-link",
				"data-test": "aged-report-link",
				href: this._linkTo(this._pathName),
			},
			this._linkLabel,
		);
	}

	_renderWarningOn(html) {
		if (!this._hasNegativeValue) {
			return;
		}

		html.div({ klass: "aged-summary-with-legend" }, [
			html.div({ klass: "aged-summary-legend legend-negative" }),
			_("Not shown in the graph because the amount is negative"),
		]);
	}

	_getColorShades(baseColor) {
		// lightening values to apply to the base color
		const shades = [120, 100, 80, 60, 40, 20, 0];
		return getColorShades(baseColor, shades);
	}
}
